import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Lock, LogOut } from 'lucide-react';
import { clearAuthAndTenant, getOrganizationById, getTenantDisplayName, getTenantId, PRODUCT_NAME } from '../lib/tenant';

export default function AccountHeld() {
  const navigate = useNavigate();
  const org = useMemo(() => {
    const tid = getTenantId();
    return tid ? getOrganizationById(tid) : undefined;
  }, []);

  useEffect(() => {
    if (localStorage.getItem('isLoggedIn') !== 'true') {
      navigate('/login', { replace: true });
      return;
    }
    if (localStorage.getItem('role') === 'SUPER_ADMIN') {
      navigate('/super-admin', { replace: true });
      return;
    }
    const tid = getTenantId();
    const o = tid ? getOrganizationById(tid) : undefined;
    if (!o?.accountHold) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const name = getTenantDisplayName() || org?.name || 'Your organization';

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-zinc-950 px-4 py-12 text-zinc-100">
      <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900/80 p-8 shadow-2xl backdrop-blur-sm">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-rose-500/30 bg-rose-500/10">
          <Lock className="h-7 w-7 text-rose-400" aria-hidden />
        </div>
        <h1 className="mt-6 text-center font-display text-xl font-semibold tracking-tight">Account access on hold</h1>
        <p className="mt-2 text-center text-sm text-zinc-400">
          {PRODUCT_NAME} — <span className="text-zinc-200">{name}</span>
        </p>
        <div className="mt-6 rounded-xl border border-zinc-700/80 bg-zinc-950/50 p-4 text-sm leading-relaxed text-zinc-300">
          <p>
            Your workspace has been temporarily suspended by the platform operator (for example, pending payment).
            You cannot open the dashboard until the hold is released.
          </p>
          {org?.accountHoldReason ? (
            <p className="mt-3 border-t border-zinc-800 pt-3 text-xs text-zinc-500">
              <span className="font-semibold text-zinc-400">Reason on file: </span>
              {org.accountHoldReason}
            </p>
          ) : null}
          {org?.accountHeldAt ? (
            <p className="mt-2 text-xs text-zinc-600">Since {new Date(org.accountHeldAt).toLocaleString()}</p>
          ) : null}
        </div>
        <div className="mt-6 flex flex-col gap-2">
          <div className="flex items-start gap-2 rounded-lg border border-zinc-800 bg-zinc-950/40 px-3 py-2 text-xs text-zinc-500">
            <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-zinc-600" aria-hidden />
            <span>Contact your billing or success contact at {PRODUCT_NAME} to resolve payment, then access will be restored.</span>
          </div>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="inline-flex w-full items-center justify-center rounded-xl border border-emerald-700/50 bg-emerald-950/40 py-3 text-sm font-semibold text-emerald-100 transition-colors hover:bg-emerald-900/50"
          >
            Check access again
          </button>
          <button
            type="button"
            onClick={() => {
              clearAuthAndTenant();
              navigate('/login');
            }}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-zinc-700 bg-zinc-800 py-3 text-sm font-semibold text-white transition-colors hover:bg-zinc-700"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
